import { useState } from "react";

import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Pill } from "../../components/Pill";
import { Select } from "../../components/Select";
import {
  type AgentCredentialKind,
  clearAgentCredential,
  setAgentCredential,
  useAgentCredential,
} from "../../data/api/account";
import { useConnections } from "../../data/api/connections";
import { Icon } from "../../icons";
import { relTime } from "../../lib/time";

// The credentials the agent uses inside a sandbox.
//
// Two of them, from two different places, which is worth making visible: the
// Claude credential is personal and entered here, while git access comes from
// the workspace's GitHub connection. A task fails for very different reasons
// depending on which is missing, so both are shown side by side.

const KIND_OPTIONS = [
  { label: "Claude subscription token", value: "oauth" },
  { label: "Anthropic API key", value: "api_key" },
];

const KIND_HELP: Record<AgentCredentialKind, string> = {
  api_key: "A platform API key from console.anthropic.com. Billed per token.",
  oauth:
    "Run `claude setup-token` and paste the result. Uses your Claude subscription.",
};

const ms = (iso: string | null): number => (iso ? Date.parse(iso) : Date.now());

export const AgentCredentials = () => {
  const { credential, loading, refetch } = useAgentCredential();
  const { connections } = useConnections();
  const [kind, setKind] = useState<AgentCredentialKind>("oauth");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const github = connections.find((c) => c.provider === "github");

  const save = (): void => {
    const token = value.trim();
    if (!token || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    void setAgentCredential(kind, token)
      .then(() => {
        setValue("");
        refetch();
      })
      .catch(() =>
        setError("Couldn't save that credential. Check it and try again."),
      )
      .finally(() => setBusy(false));
  };

  const clear = (): void => {
    setBusy(true);
    void clearAgentCredential()
      .then(refetch)
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Agent credentials</h3>
      <div className={styles.card}>
        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.label}>
              Claude{" "}
              {credential?.configured ? (
                <Pill status="done">Configured</Pill>
              ) : loading ? null : (
                <Pill status="needs-input">Not set</Pill>
              )}
            </div>
            <div className={styles.help}>
              {credential?.configured
                ? `${credential.kind === "oauth" ? "Subscription token" : "API key"} ending …${credential.hint ?? "????"} · updated ${relTime(ms(credential.updated_at))}`
                : "Sandboxes you start have no way to authenticate until you add one."}
            </div>
          </div>
          {credential?.configured ? (
            <Button disabled={busy} onClick={clear} size="sm" variant="ghost">
              Remove
            </Button>
          ) : null}
        </div>

        <div className={styles.row}>
          <Select
            onValueChange={(v) => setKind(v as AgentCredentialKind)}
            options={KIND_OPTIONS}
            value={kind}
          />
          <Input
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              }
            }}
            placeholder={kind === "oauth" ? "sk-ant-oat…" : "sk-ant-api…"}
            style={{ flex: 1 }}
            type="password"
            value={value}
          />
          <Button
            disabled={!value.trim() || busy}
            onClick={save}
            size="sm"
            variant="accent"
          >
            {credential?.configured ? "Replace" : "Save"}
          </Button>
        </div>
        <div className={styles.row}>
          <span className={styles.help}>{KIND_HELP[kind]}</span>
        </div>
        {error ? (
          <div className={styles.row}>
            <span className={styles.help}>⚠ {error}</span>
          </div>
        ) : null}

        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.label}>
              GitHub{" "}
              {github ? (
                <Pill status="done">Connected</Pill>
              ) : (
                <Pill status="needs-input">Not connected</Pill>
              )}
            </div>
            <div className={styles.help}>
              {github
                ? "Sandboxes get a short-lived token from this connection, so the agent can fetch and push."
                : "Connect GitHub below — without it an agent can clone nothing and push nothing."}
            </div>
          </div>
          {github ? null : (
            <span className={styles.help}>
              <Icon.ArrowDown size={13} /> Connections
            </span>
          )}
        </div>
      </div>
    </section>
  );
};
